import React from 'react'
import {
  Bell,
  ClipboardCheck,
  MailCheck,
  MessageCircle,
  MessageCircleWarning,
  MessagesSquare,
  Wrench,
} from 'lucide-react'
import { INotification } from 'services/notifications/types'

export interface IServiceVisual {
  label: string
  text: string
  border: string
  soft: string
  icon: React.ReactNode
}

const CHAT_VISUAL: IServiceVisual = {
  label: 'chat',
  text: '#16A34A',
  border: '#D2F0DD',
  soft: '#E1F3E7',
  icon: <MessagesSquare size={22} />,
}

// One glyph + one tone per service. The core colors/icons mirror the mobile News Feed create menu
// so creation and notifications speak the same visual language.
export const SERVICE_VISUALS: Record<string, IServiceVisual> = {
  tasks: {
    label: 'tasks',
    text: '#7C3AED',
    border: '#DDD3FE',
    soft: '#EEE5FD',
    icon: <ClipboardCheck size={22} />,
  },
  posts: {
    label: 'posts',
    text: '#2563EB',
    border: '#C7D7FE',
    soft: '#E2EAFC',
    icon: <MailCheck size={22} />,
  },
  comments: {
    label: 'comments',
    text: '#B45309',
    border: '#FCE4B6',
    soft: '#FFFBF2',
    icon: <MessageCircle size={22} />,
  },
  service_calls: {
    label: 'service_call_section_titie',
    text: '#DC2626',
    border: '#F8D4D4',
    soft: '#FCE4E4',
    icon: <Wrench size={22} />,
  },
  pops: {
    label: 'pops',
    text: '#EA580C',
    border: '#FBD8C7',
    soft: '#FDEBE1',
    icon: <MessageCircleWarning size={22} />,
  },
}

// Unknown service value → slate tone + bell glyph. Must never block rendering — a new backend
// service type should not require a frontend release before it's merely visible.
export const FALLBACK_SERVICE_VISUAL: IServiceVisual = {
  label: 'Notification',
  text: '#475569',
  border: '#DFE4EA',
  soft: '#F6F7F9',
  icon: <Bell size={18} />,
}

export const getServiceVisual = (service: string, isRtl?: boolean): IServiceVisual => {
  const visual = SERVICE_VISUALS[service] ?? FALLBACK_SERVICE_VISUAL
  if (service === 'comments' && isRtl) {
    return {
      ...visual,
      icon: <span style={{ display: 'inline-flex', transform: 'scaleX(-1)' }}>{visual.icon}</span>,
    }
  }
  return visual
}

const firstMeaningfulString = (...values: unknown[]): string | undefined => {
  const value = values.find((candidate) => typeof candidate === 'string' && candidate.trim())
  return typeof value === 'string' ? value.trim() : undefined
}

const isChatPostNotification = (notification: INotification): boolean => {
  if (notification.service !== 'posts') return false

  const objectRef = notification.object_ref ?? notification.data?.object_ref ?? notification.data?.objectRef ?? {}
  const data = notification.data ?? {}
  const postType = firstMeaningfulString(notification.post_type, objectRef.post_type, data.post_type)

  return postType?.toLowerCase() === 'chat'
}

export const getNotificationVisual = (notification: INotification, isRtl?: boolean): IServiceVisual => {
  if (isChatPostNotification(notification)) return CHAT_VISUAL

  return getServiceVisual(notification.service, isRtl)
}

// Services with no real backend data yet — shown as a dashed placeholder in the rail so the
// information architecture is visible ahead of time without implying they work today.
export const UPCOMING_SERVICES = ['service_call_section_titie', 'tasks', 'posts', 'pops', 'comments']
